import { forwardRef, useState, useCallback, type Ref, type KeyboardEvent } from 'react';
import CoreInput, { type InputProps as CoreInputProps } from '@ovpn/ui/input';
import Label, { type LabelOwnProps } from '@ovpn/ui/label';

import { boxed } from '@sso/shared/hocs';

import Icon from './Icon';

type PasswordProps = CoreInputProps & Omit<LabelOwnProps, 'children' | 'as'>;

function Password(
    {
        description,
        appearance,
        className,
        layout,
        title,
        size,
        dir,

        onKeyDown,
        onKeyUp,

        ...inputProps
    }: PasswordProps,
    ref: Ref<HTMLInputElement>,
) {
    const [capsLock, setCapsLock] = useState(false);
    const [visible, setVisible] = useState(false);

    const toggleVisibility = useCallback(() => setVisible(!visible), [visible]);

    const handleKeyUp = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            setCapsLock(e.getModifierState('CapsLock'));
            onKeyUp?.(e);
        },
        [onKeyUp],
    );

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            setCapsLock(e.getModifierState('CapsLock'));
            onKeyDown?.(e);
        },
        [onKeyDown],
    );

    const type = visible ? 'text' : 'password';
    const icon = visible ? 'eye-off' : 'eye';

    const capsDescription = capsLock ? 'Caps Lock is on' : description;

    return (
        <Label
            appearance={inputProps.invalid ? 'warning' : appearance}
            required={inputProps.required}
            description={capsDescription}
            data-testid={inputProps.id}
            className={className}
            layout={layout}
            title={title}
            size={size}
            dir={dir}
        >
            <CoreInput
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
                {...inputProps}
                type={type}
                ref={ref}
                iconRight={
                    <Icon
                        style={{ cursor: 'pointer' }}
                        onClick={toggleVisibility}
                        name={icon}
                        size="md"
                    />
                }
            />
        </Label>
    );
}

export default boxed(forwardRef(Password));
